import React, { useEffect, useRef, useState } from "react";
import "./TagChipEditor.css";

const getTagName = (item, targetKey) =>
  (typeof item === "string" ? item : item?.[targetKey])?.toString();

// Shared chip-list tag editor for doc/link-level tags (KDF listing, KDF
// settings, LaserLINK listing): one bordered box holding the tag chips and
// an always-visible trailing text input, wrapping onto new lines together —
// no click-to-reveal step, no popover.
//
// Callers get one `onChange(nextTags)` with the full desired tag list
// (existing {id,name} objects plus any newly typed name as a plain string)
// and are responsible for resolving/persisting it — see utils/resolveTags.
const TagChipEditor = ({
  tags = [],
  onChange = () => {},
  canEdit = true,
  limit = null,
  targetKey = "name",
  placeholderText = "Add a tag...",
  className = "",
  minWidthCh = 9,
  collapsible = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);

  const atLimit = limit != null && (tags?.length || 0) >= limit;
  const hasTags = (tags?.length || 0) > 0;
  const collapsed = collapsible && hasTags && !isOpen;
  const showInput = !atLimit && !collapsed;
  const showAddButton = collapsible && hasTags && !isOpen && !atLimit;

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const openInput = () => {
    if (atLimit) return;
    setIsOpen(true);
  };

  const collapseIfPossible = () => {
    if (collapsible && (tags?.length || 0) > 0) setIsOpen(false);
  };

  const existingNames = new Set(
    (tags || [])
      .map((tag) => getTagName(tag, targetKey)?.trim().toLowerCase())
      .filter(Boolean)
  );

  const addTag = (rawValue) => {
    const name = (rawValue || "").trim();
    if (!name || atLimit || existingNames.has(name.toLowerCase())) {
      setInputValue("");
      return;
    }
    onChange([...(tags || []), name]);
    setInputValue("");
    if (collapsible) setIsOpen(false);
  };

  const removeTag = (indexToRemove) => {
    onChange((tags || []).filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === "Tab" || event.key === ",") {
      if (inputValue.trim()) {
        event.preventDefault();
        addTag(inputValue);
      } else if (event.key === "Tab") {
        collapseIfPossible();
      }
      return;
    }
    if (event.key === "Escape") {
      setInputValue("");
      collapseIfPossible();
      return;
    }
    if (event.key === "Backspace" && inputValue === "" && (tags?.length || 0) > 0) {
      removeTag(tags.length - 1);
    }
  };

  if (!canEdit) {
    if (!tags?.length) return null;
    return (
      <span className={`tag-chip-editor-box tag-chip-editor-readonly ${className}`}>
        {tags.map((tag, index) => (
          <span key={tag?.id ?? `${getTagName(tag, targetKey)}-${index}`} className="tag-primary">
            {getTagName(tag, targetKey)}
          </span>
        ))}
      </span>
    );
  }

  return (
    <span
      className={`tag-chip-editor-box ${className}`}
      onClick={() => (collapsed ? openInput() : inputRef.current?.focus())}
    >
      {(tags || []).map((tag, index) => (
        <span key={tag?.id ?? `${getTagName(tag, targetKey)}-${index}`} className="tag-primary">
          {getTagName(tag, targetKey)}
          <button
            type="button"
            className="tag-remove-btn"
            aria-label={`Remove tag ${getTagName(tag, targetKey)}`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              removeTag(index);
            }}
          >
            ×
          </button>
        </span>
      ))}
      {showAddButton && (
        <button
          type="button"
          className="tag-chip-editor-add"
          aria-label="Add a tag"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openInput();
          }}
        >
          <img src="/icons/add_2.svg" width="14" alt="" />
        </button>
      )}
      {showInput && (
        <input
          ref={inputRef}
          type="text"
          className="tag-chip-editor-input"
          placeholder={placeholderText}
          value={inputValue}
          maxLength={32}
          style={{
            width: `${Math.min(Math.max(inputValue.length + 1, minWidthCh), 32)}ch`,
          }}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            addTag(inputValue);
            collapseIfPossible();
          }}
        />
      )}
    </span>
  );
};

export default TagChipEditor;
